import React, {createContext, Dispatch, SetStateAction, useContext, useEffect, useState} from 'react'
import SearchService from './SearchService';
import {
  IHandleQuickSearchResult,
  ISearchProviderConfig,
  IQuickSearchResultTypo,
  ISearchAndFilterProduct,
  IQuickSearchResultCollection,
  IQuickSearchResultSuggestion,
  IQuickSearchResultContent,
  ISearchFilter,
  ISearchFilterOption,
  IQuickSearchResultPage,
  ISearchFilterRangeOption,
  IActiveCollection, IGetProductsResult,
  IGetCmsContentResult
} from './types';
import {useDebouncedCallback} from 'use-debounce';
import updateLocationQuery from './helpers/updateLocationQuery';
import getCurrentSelectedFilters from './helpers/getCurrentSelectedFilters';


import FILTER_SEARCH from './consts';
import extenders from './extenders';
export interface IFFetchContext {
  children: any,
  config: ISearchProviderConfig,
  activeCollection: IActiveCollection
}

interface ContextValue {
  activeCollection: IActiveCollection,
  searchTerm: string,
  quickSearchTerm: string,
  setQuickSearchTerm: Dispatch<SetStateAction<string>>,
  sortKey: string,
  setSortKey: Dispatch<SetStateAction<string>>,
  pageIndex: number,
  isSearching: boolean,
  quickSearchResult: IHandleQuickSearchResult,
  quickSearchResultsTypos: IQuickSearchResultTypo[],
  quickSearchResultsProducts: ISearchAndFilterProduct[],
  quickSearchResultsCollections: IQuickSearchResultCollection[],
  quickSearchResultsPages: IQuickSearchResultPage[],
  quickSearchResultsSuggestions: IQuickSearchResultSuggestion[],
  quickSearchResultsContent: IQuickSearchResultContent[],
  searchFilters: ISearchFilter[],
  filtersLoading: boolean,
  productSearchResult: IGetProductsResult,
  productSearchResultsState: IGetProductsResult[],
  cmsSearchResults: IGetCmsContentResult,
  inputEventHandlers: {
    handleSearchInputChange: (any),
    handleSearchInputKeyPress: (any),
    handleSearchSuggestionSelected: (any),
    handleUpdateRangeOption: (filterIndex: number, optionIndex: number, selectedMinimum: number, selectedMaximum: number) => void,
    handleToggleMultiOption: (any),
    handleToggleSingleOption: (any),
    handleUpdatePageIndex: (pageIndex: number, triggerFiltering: boolean) => void,
    handleSortByChange: (any),
    handleButtonPress: (any),
    handleClearFilters: (any)
  },
  config: ISearchProviderConfig,
  showClear: boolean,
}

export const FILTER_QUERY_PARAM_PREFIX = 'filter_'
export const FILTER_QUERY_PARAM_SEPARATOR = ','

export const SORT_TYPE_VALS = Object.values(FILTER_SEARCH.SORT_TYPES)

export const SearchContext: React.Context<ContextValue> = createContext(null)

export const SearchProvider: React.FC<IFFetchContext> = ({ children, config, activeCollection }) => {

  // control
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [showClear, setShowClear] = useState<boolean>(false)


  // input
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [quickSearchTerm, setQuickSearchTerm] = useState<string>('')
  const [pageIndex, setPageIndex] = useState<number>(null)

  // data
  const [quickSearchResult, setQuickSearchResult] = useState<IHandleQuickSearchResult>(null)
  const [quickSearchResultsTypos, setQuickSearchResultsTypos] = useState<IQuickSearchResultTypo[]>(null)
  const [quickSearchResultsProducts, setQuickSearchResultsProducts] = useState<ISearchAndFilterProduct[]>(null)
  const [quickSearchResultsCollections, setQuickSearchResultsCollections] = useState<IQuickSearchResultCollection[]>(null)
  const [quickSearchResultsSuggestions, setQuickSearchResultsSuggestions] = useState<IQuickSearchResultSuggestion[]>(null)
  const [quickSearchResultsContent, setQuickSearchResultsContent] = useState<IQuickSearchResultContent[]>(null)
  const [quickSearchResultsPages, setQuickSearchResultsPages] = useState<IQuickSearchResultPage[]>(null)

  const [searchFilters, setSearchFilters] = useState<ISearchFilter[]>(null)

  const [sortKey, setSortKey] = useState<string>(null)

  const [productSearchResult, setProductSearchResult] = useState<IGetProductsResult>(null)
  const [productSearchResultsState, setProductResultsState] = useState<IGetProductsResult[]>([])

  const [cmsSearchResults, setCmsSearchResults] = useState<IGetCmsContentResult>(null)

  const [filtersLoading, setFiltersLoading] = useState<boolean>(false)

  const updateFilters = (newFilters: ISearchFilter[]): void => {

    const updatedFilters = searchFilters.map(filter => {

      const newFilter = newFilters.find(({ identifier }) => identifier === filter.identifier)

      if (filter.type === FILTER_SEARCH.FILTER_TYPES.RANGE) {
        // keep range the same
        // @TODO, we could update the mins and maxes here?
        return filter
      }

      const oldOptions = filter.options as ISearchFilterOption[] // as not range type
      const newOptions = newFilter ? newFilter.options as ISearchFilterOption[] : []
      
      return {
        ...filter,
        options: oldOptions.map((option) => {
          const newOption = newOptions.find(({identifier}) => identifier && identifier === option.identifier) 
          return {
            ...option,
            records: newOption ? newOption.records : 0
          }
        })
      }
    })
    
    setSearchFilters([...updatedFilters])
  }

  const wipeLiquidProducts = (): void => {
    const liquidProducts = document.querySelector('[data-liquid-products]')
    if (liquidProducts) {
      liquidProducts.innerHTML = '';
    }
  }

  const executeSearch = useDebouncedCallback((term) => {

    SearchService.methods.handleSearch(term, config).then((response) => {
      setIsSearching(false)
      setQuickSearchResult(response)
      setQuickSearchResultsTypos(response.typos)
      setQuickSearchResultsProducts(response.products)
      setQuickSearchResultsCollections(response.collections)
      setQuickSearchResultsSuggestions(response.suggestions)
      setQuickSearchResultsPages(response.pages)
      setQuickSearchResultsContent(null) // @todo
    })
  }, 1000)

  const executeFilteredSearch = useDebouncedCallback(() => {

    if (pageIndex && productSearchResultsState[pageIndex]) {
      wipeLiquidProducts();
      setProductSearchResult(productSearchResultsState[pageIndex])
      setFiltersLoading(false)
    } else {
      SearchService.methods.getProductResults(
        config,
        searchFilters,
        activeCollection,
        { term: searchTerm, pageIndex: pageIndex, sortKey: sortKey }
      ).then((response) => {

        wipeLiquidProducts();
        setProductSearchResult(response)
        if (response.filters) {
          updateFilters(response.filters)
          setFiltersLoading(false)
          if (window['ReloadSmartWishlist']){
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
            window.ReloadSmartWishlist()
          }
        }
        if (pageIndex) {
          const state = productSearchResultsState
          state[pageIndex] = response
          setProductResultsState([...state])
          setFiltersLoading(false)
        }
      })
    }
  }, 1000)

  const setLoadingAndExecuteFilteredSearch = () => {
    
    const liquidProducts = document.querySelector('[data-liquid-products]')

    if (liquidProducts) {
      liquidProducts.classList.add('is-loading')
    }

    setFiltersLoading(true)
    
    executeFilteredSearch.callback()
  }

  const updatePageIndex = (pageIndex: number, triggerFiltering: boolean, historyMethod: 'pushState' | 'replaceState' = 'pushState') => {
    // update state
    setPageIndex(pageIndex)

    // set url param
    updateLocationQuery({
      key: 'page',
      historyMethod,
      value: pageIndex.toString(),
      action: 'set'
    })

    // re-filter
    if (triggerFiltering) {
      executeFilteredSearch.callback();
    }
  }

  const eventHandlers = {
    filterUpdated: (params: {
      filterIndex: number,
      optionIndex: number,
      updatedSearchFilters: ISearchFilter[]
    }) => {
      // get the filter
      const filter = searchFilters[params.filterIndex]

      if (['SINGLE_OPTION', 'MULTI_OPTION'].includes(filter.type)) {

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const options: ISearchFilterOption[] = filter.options.map((option: ISearchFilterOption) => {
          return option.selected ? option.identifier : false
        }).filter(option => option)

        if (options.length) {
          updateLocationQuery({
            key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
            value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
            action: 'set'
          })
        } else {
          if (options.length > 0) {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
              action: 'set'
            })
          } else {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              action: 'delete'
            })
          }
        }
      } else if (['RANGE'].includes(filter.type)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const option: ISearchFilterRangeOption = filter.options[params.optionIndex]

        updateLocationQuery({
          key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
          value: `${option.selectedMinimum}:${option.selectedMaximum}`,
          action: 'set'
        })
      } else {
        throw 'Unhandled filter type'
      }

      setSearchFilters([...searchFilters])
      updatePageIndex(1, false, 'replaceState')

      // invalidate paginated state
      setProductResultsState([])
    },
    sortByUpdated: (
      params: {
        sortKey: string
      }
    ) => {
      
      if (!SORT_TYPE_VALS.includes(params.sortKey)) {
        console.info('Incorrect sort type')
      }

      updateLocationQuery({
        key: 'sort',
        value: params.sortKey,
        action: 'set'
      })
      setSortKey(params.sortKey)
      updatePageIndex(1, false, 'replaceState')
      // invalidate paginated state
      setProductResultsState([])
    } 
  }



  /**
   * Input event handlers
   */
  const inputEventHandlers = {

    handleSearchInputChange: (e) => {

      const query = e.target.value

      setQuickSearchTerm(query)

      // result all search results if the search field has been emptied
      if (query.length === 0) {
        setQuickSearchResult(null)
        setQuickSearchResultsTypos(null)
        setQuickSearchResultsProducts(null)
        setQuickSearchResultsCollections(null)
        setQuickSearchResultsSuggestions(null)
        setQuickSearchResultsContent(null)
        return
      }

      setIsSearching(true)

      executeSearch.callback(query)
    },

    handleSearchInputKeyPress: (e) => {
      if (e.keyCode === 13) {
        // Canonical search URL: lower-cased term + type=product.
        // - type=product because the results page renders products only; without
        //   it Shopify also returns pages/articles and seo_title counts them.
        // - toLowerCase() because Shopify applies query expansion (Search &
        //   Discovery synonyms) case-sensitively, so "African" returns far fewer
        //   products than "african". Mobile keyboards auto-capitalise, so this
        //   would otherwise shrink results for most phone users.
        // Linking to the canonical form avoids the results page having to
        // redirect, which would flash a wrong count in the tab title first.
        window.location.href = `/search?q=${quickSearchTerm.toLowerCase()}&type=product`
      }
    },

    handleSearchSuggestionSelected: (index) => {

      // remove any HTML which may have been added to the suggestion result
      const suggestion = quickSearchResultsSuggestions[index].value.replace(/(<([^>]+)>)/gi, '')
      setIsSearching(true)
      setSearchTerm(suggestion)
      executeSearch.callback(suggestion)
    },

    handleUpdateRangeOption: (filterIndex: number, optionIndex: number, selectedMinimum: number, selectedMaximum: number) => {

      const updatedOption = [...searchFilters][filterIndex].options[optionIndex] as ISearchFilterRangeOption
      updatedOption.selectedMinimum = selectedMinimum.toString();
      updatedOption.selectedMaximum = selectedMaximum.toString();
      updatedOption.selected = !(updatedOption.selectedMinimum === updatedOption.minimum && updatedOption.selectedMaximum === updatedOption.maximum)

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      // execute filter updated event
      eventHandlers.filterUpdated({
        filterIndex: filterIndex,
        optionIndex: optionIndex,
        updatedSearchFilters
      })
      updatedOption.selected ? setShowClear(true) : setShowClear(false)
      // execute search
      setLoadingAndExecuteFilteredSearch()
    },

    handleToggleMultiOption: (filterIndex: number, optionIndex: number) => {


      const updatedOption = [...searchFilters][filterIndex].options[optionIndex] as ISearchFilterOption

      updatedOption.selected = !updatedOption.selected

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      // execute filter updated event
      eventHandlers.filterUpdated({
        filterIndex: filterIndex,
        optionIndex: optionIndex,
        updatedSearchFilters
      })
      updatedOption.selected ? setShowClear(true) : setShowClear(false)
      setLoadingAndExecuteFilteredSearch()
    },

    handleToggleSingleOption: (filterIndex: number, optionIndex: number, value: boolean) => {

      const updatedFilterOptions = [...searchFilters][filterIndex].options as ISearchFilterOption[];

      for (let i = 0; i < updatedFilterOptions.length; i++) {
        updatedFilterOptions[i].selected = false
      }

      updatedFilterOptions[optionIndex].selected = value

      const updatedSearchFilters = [...searchFilters]
      updatedSearchFilters[filterIndex].options = updatedFilterOptions;

      // execute filter updated event
      eventHandlers.filterUpdated({
        filterIndex: filterIndex,
        optionIndex: optionIndex,
        updatedSearchFilters
      })
      value ? setShowClear(true) : setShowClear(false)
      setLoadingAndExecuteFilteredSearch()
    },

    handleUpdatePageIndex: (pageIndex: number, triggerFiltering: boolean) => {
      updatePageIndex(pageIndex, triggerFiltering, 'pushState')
    },

    handleSortByChange: (val: string) => {
      eventHandlers.sortByUpdated({
        sortKey: val
      })
      setLoadingAndExecuteFilteredSearch()
    },

    handleButtonPress: () => {
      //Close side drawer
      document.body.classList.toggle('filters-open')
    },

    handleClearFilters: () => {
      const indices = []
      searchFilters.forEach((filter, index:number) => {
        const ticked = (filter.options as ISearchFilterOption[]).map(({selected}, i) => ({selected, i}))
        if (ticked.length) {
          for (const selected of ticked) {
            if (!selected.selected) continue;
            indices.push({index, int: selected.i})
          }
        }
      })
      if (!indices.length) {
        return null
      }
      indices.forEach(({ index, int }) => inputEventHandlers.handleToggleMultiOption(index, int))
      //Handle clearing Rangeslider
      const range = indices.find(({ index }) => searchFilters[index].type === 'RANGE');
      if (range) {
        const rangeSlider = searchFilters[range.index];
        const option = rangeSlider.options[range.int] as ISearchFilterRangeOption
        option.selectedMaximum = option.maximum;
        option.selectedMinimum = option.minimum;
        //Update URL
        updateLocationQuery({
          key: `${FILTER_QUERY_PARAM_PREFIX}${rangeSlider.identifier}`,
          action: 'delete'
        })
      }
      setShowClear(false)
      searchFilters.forEach(filter => {
        filter.initiallyOpen = false
      })
      document.body.classList.toggle('filters-open')
    }
  }


  const getCurrentUrlWithTagSet = (tag: string, val: boolean) => {
    const path = window.location.pathname.split('/').filter(part => part);
    let newPath = `/${ path.join('/')}`
    if (val && path[path.length - 1] !== tag) { // needs to add
      newPath = `/${path.join('/')}/${tag}`
    } else if (!val && path[path.length - 1] === tag) { // needs to remove
      newPath = `/${path.slice(0, -1).join('/')}`
    }
    return `${window.location.origin}${newPath}${window.location.search}${window.location.hash}`;
  }
  
  const handlePopState = () => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('page')
    const page = pageParam && !isNaN(parseInt(pageParam)) ? parseInt(pageParam) : 1;
    if (page) {
      updatePageIndex(page, true, 'replaceState')
    }
  }

  //on render
  useEffect(() => {
    window.addEventListener('popstate', () => handlePopState());
  }, [])

  /**
   * Initialise Search Filters
   */
  useEffect(() => {
    // parse query params for search context
    const params = new URLSearchParams(window.location.search)

    let executeSearch = false
    let localSearchTerm = null;
    if (!searchTerm) {
      if (params.get('q')) {
        localSearchTerm = params.get('q')
        setSearchTerm(localSearchTerm)
        executeSearch = true
      }
    }

    if (!pageIndex) {
      if (params.get('page')) setPageIndex(parseInt(params.get('page')))
      else setPageIndex(1)
    }

    if (!sortKey) {
      if (params.get('sort')) {
        
        setSortKey(params.get('sort'))
        executeSearch = true
      }
    }

    if (!cmsSearchResults && params.get('q')) {
      SearchService.methods.getCmsContent(params.get('q'), config).then((response) => {
        setCmsSearchResults(response)
      }).catch((error) => {
        console.error(error)
        // @todo handle error for getting cms content
      })
    }

    // @todo we should only really be calling this if we're on a search rules / collection page
    if (!searchFilters) {
    
      SearchService.methods.getFilters(config, activeCollection, localSearchTerm).then((response) => {

        // parse relevant filters from URL
        const selectedFilters = []
        params.forEach((values, key) => {
          if (key.startsWith(FILTER_QUERY_PARAM_PREFIX)) {
            values.split(',').forEach((value) => {
              selectedFilters.push({
                filterKey: key,
                optionKey: value
              })
            })
          }
        })

        // Initialise filter states
        if (selectedFilters.length > 0) {
          
          selectedFilters.forEach((selectedFilter: {
            filterKey: string,
            optionKey: string
          }) => {
            // find the filter index
            const filterIndex = response.filters.findIndex((filter) => {
              return `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}` === selectedFilter.filterKey
            })

            if (filterIndex === -1) return

            // set initially opened state of the filter (for FE display)
            response.filters[filterIndex].initiallyOpen = true

            // toggle the option
            if (['SINGLE_OPTION', 'MULTI_OPTION'].includes(response.filters[filterIndex].type)) {

              // find the option index
              const optionIndex = response.filters[filterIndex].options.findIndex((option) => {
                return option.identifier === selectedFilter.optionKey
              })

              if (optionIndex >= 0) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                response.filters[filterIndex].options[optionIndex].selected = true
                executeSearch = true
              }
            } else if (['RANGE'].includes(response.filters[filterIndex].type)) {

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              response.filters[filterIndex].options[0].selectedMinimum = selectedFilter.optionKey.split(':')[0]

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              response.filters[filterIndex].options[0].selectedMaximum = selectedFilter.optionKey.split(':')[1]

              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              response.filters[filterIndex].options[0].selected = true

              executeSearch = true
            }
          })
        }

        setSearchFilters(response.filters)

        if (executeSearch) setLoadingAndExecuteFilteredSearch()
        
      })
    }

    const selectedFilters = searchFilters ? getCurrentSelectedFilters(searchFilters) : [];
    const updateUrlWithTag = Boolean(activeCollection.handle && (selectedFilters.length > 0 || sortKey))
 
    const url = getCurrentUrlWithTagSet(config.searchFilterDummyTag, (updateUrlWithTag))
    if (window.history) {
      window.history.replaceState({ ...window.history.state }, '', url)
    }
    
  }, [searchFilters, searchTerm, sortKey])

  useEffect(() => {
    if (searchFilters && searchFilters.length) {
      //Set filter active count
      const getActiveCount = getCurrentSelectedFilters(searchFilters)
      setShowClear(getActiveCount.length ? true : false)
    }
    
  }, [searchFilters])

  useEffect(() => {
    if (productSearchResult) {
      //extend the data if we need to
      let result = { ...productSearchResult }
      for (const extender of extenders) {
        if (typeof (extender.init) !== 'undefined') {
          extender.init()
        }
        if (typeof (extender.extendGetProductsResult) !== 'undefined') {
          result = extender.extendGetProductsResult(result)
        }
      }
      // NOTE: this used to rewrite the search page's <title> with this
      // provider's own total:
      //   document.title = document.title.replace(/\d+/g, productSearchResult.total)
      // The search results page is now rendered natively and Shopify's
      // seo_title already reports the correct, product-scoped count, so
      // overwriting it only ever made the tab title wrong (it stamped in this
      // provider's total, and /\d+/g clobbered every number in the title).
      setProductSearchResult(result)
    }
  }, [productSearchResultsState])



  return (
    <SearchContext.Provider value={{
      activeCollection,
      searchTerm,
      quickSearchTerm,
      setQuickSearchTerm,
      pageIndex,
      quickSearchResult,
      quickSearchResultsTypos,
      quickSearchResultsSuggestions,
      quickSearchResultsProducts,
      quickSearchResultsCollections,
      quickSearchResultsContent,
      quickSearchResultsPages,
      searchFilters,
      filtersLoading,
      sortKey,
      setSortKey,
      productSearchResult,
      productSearchResultsState,
      cmsSearchResults,
      isSearching,
      inputEventHandlers,
      config,
      showClear,
    }}>
      {children}
    </SearchContext.Provider>
  )
}

const useSearch = (): ContextValue => useContext(SearchContext)

export default useSearch